/**
 * Venom Altar hero props (pure): each authored hint (DungeonDef.props) becomes a small
 * composition of kit models and procedural pieces (procs.ts) in the hint's local frame
 * (local +z = the hint's facing, toward the room). Deterministic per hint.
 */
import type { HeroContext, HeroExpander, Placement } from '../../dressing/dress';
import { yawToward } from '../../dressing/dress';
import type { PlacedHint } from '../../dressing/arch';
import type { RGB } from '../types';

const WHITE: RGB = [1, 1, 1];
const GOLD: RGB = [1.15, 0.95, 0.6];
const BONE: RGB = [0.95, 0.92, 0.84];

type Local = { lx: number; lz: number };
/** Sim position of a point in the hint's local frame (lx: left, lz: forward). */
function at(h: PlacedHint, l: Local): { x: number; y: number } {
  const fx = Math.cos(h.yaw);
  const fy = Math.sin(h.yaw);
  return { x: h.pos.x + -fy * l.lx * h.scale + fx * l.lz * h.scale, y: h.pos.y + fx * l.lx * h.scale + fy * l.lz * h.scale };
}
const faceYaw = (h: PlacedHint, extra = 0): number => yawToward(Math.cos(h.yaw), Math.sin(h.yaw)) + extra;

function put(h: PlacedHint, ctx: HeroContext, asset: string, l: Local, o: Partial<Omit<Placement, 'pos'>> & { dy?: number } = {}): void {
  const pos = at(h, l);
  const scale = (o.scale ?? 1) * h.scale;
  ctx.place({
    asset,
    pos,
    y: (o.y ?? ctx.surface(pos)) + (o.dy ?? 0) * h.scale,
    yaw: o.yaw ?? faceYaw(h),
    scale,
    tint: o.tint ?? WHITE,
    layer: o.layer ?? 'hero',
    // r / h are absolute yards at hint scale 1 (r: the smallest half-extent, used for clearance).
    r: (o.r ?? 0.5) * h.scale,
    h: (o.h ?? 1) * h.scale,
    castShadow: o.castShadow ?? true,
    ...(o.stretch ? { stretch: o.stretch } : {}),
    ...(o.tilt !== undefined ? { tilt: o.tilt } : {}),
  });
}

const scatterAround = (h: PlacedHint, ctx: HeroContext, assets: readonly string[], n: number, radius: number, scale: [number, number], tint: RGB = WHITE): void => {
  for (let i = 0; i < n; i++) {
    const a = ctx.rnd() * Math.PI * 2;
    const r = radius * (0.5 + ctx.rnd() * 0.5);
    put(h, ctx, assets[i % assets.length]!, { lx: Math.cos(a) * r, lz: Math.sin(a) * r * 0.6 }, { scale: scale[0] + ctx.rnd() * (scale[1] - scale[0]), yaw: ctx.rnd() * 6.28, layer: 'detail', tint, castShadow: false, r: 0.3, h: 0.3 });
  }
};

export const VENOM_HEROES: Record<string, HeroExpander> = {
  ruined_gate: (h, ctx) => {
    put(h, ctx, 'proc:ruined_gate', { lx: 0, lz: -0.2 }, { r: 1.0, h: 3.2 });
    put(h, ctx, 'kn_rock_largeA', { lx: 4.3, lz: 0.6 }, { scale: 4.2, yaw: 1.2, r: 0.5, h: 0.3 });
    put(h, ctx, 'kn_rock_largeC', { lx: 5.2, lz: -0.2 }, { scale: 3.8, yaw: 2.6, r: 0.5, h: 0.3 });
    put(h, ctx, 'kn_statue_columnDamaged', { lx: -5.0, lz: 0.4 }, { scale: 4.0, yaw: 0.4, tilt: 1.35, r: 0.4, h: 0.7, dy: 0.5 });
    scatterAround(h, ctx, ['kn_plant_bushDetailed', 'kn_plant_flatTall', 'kn_grass_large'], 5, 4.6, [3.4, 4.4], [0.85, 1.0, 0.75]);
  },
  cobra_statue: (h, ctx) => {
    put(h, ctx, 'proc:cobra', { lx: 0, lz: 0 }, { r: 1, h: 4.2 });
    put(h, ctx, 'kd_candle_triple', { lx: 0.7, lz: 0.9 }, { scale: 1.4, layer: 'detail', castShadow: false, r: 0.2, h: 0.9 });
    put(h, ctx, 'kn_plant_bush', { lx: -0.9, lz: 0.6 }, { scale: 3.8, layer: 'detail', castShadow: false, tint: [0.85, 1.0, 0.75], r: 0.2, h: 0.3 });
  },
  cage: (h, ctx) => {
    // Front bars facing the room, half-height side bars, straw and a coiled serpent inside.
    put(h, ctx, 'kd_barrier_column', { lx: 0, lz: 1.35 }, { scale: 1.15, r: 0.4, h: 1.6 });
    put(h, ctx, 'kd_barrier_half', { lx: 2.3, lz: 1.35 }, { scale: 1.15, yaw: faceYaw(h, Math.PI / 2), r: 0.3, h: 1.3 });
    put(h, ctx, 'kd_barrier_half', { lx: -2.3, lz: -0.95 }, { scale: 1.15, yaw: faceYaw(h, -Math.PI / 2), r: 0.3, h: 1.3 });
    put(h, ctx, 'proc:cage_top', { lx: 0, lz: 0.1 }, { r: 1.4, h: 2.4 });
    put(h, ctx, 'proc:straw', { lx: 0.3, lz: -0.1 }, { scale: 1.3, layer: 'detail', castShadow: false, r: 0.8, h: 0.3 });
    put(h, ctx, 'proc:snake', { lx: -0.4, lz: 0 }, { scale: 0.9, yaw: ctx.rnd() * 6.28, r: 0.8, h: 0.6 });
    if (ctx.rnd() < 0.5) put(h, ctx, 'kh_bone_C', { lx: 1.2, lz: 0.4 }, { scale: 1.3, yaw: 1, layer: 'detail', castShadow: false, tint: BONE, dy: 0.1 });
  },
  ribcage: (h, ctx) => {
    put(h, ctx, 'kh_ribcage', { lx: 0, lz: 0 }, { scale: 3.4, dy: 1.2, tint: BONE, r: 1.3, h: 2.6 });
    put(h, ctx, 'kh_skull', { lx: 1.9, lz: 0.6 }, { scale: 1.9, yaw: faceYaw(h, 0.6), tint: BONE, r: 0.6, h: 1 });
    scatterAround(h, ctx, ['kh_bone_A', 'kh_bone_C', 'kh_bone_A'], 6, 2.4, [1.2, 1.6], BONE);
  },
  skull_mound: (h, ctx) => {
    put(h, ctx, 'proc:mound', { lx: 0, lz: 0 }, { r: 1.5, h: 1.1 });
    const n = 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + ctx.rnd() * 0.4;
      const r = i === 0 ? 0 : 0.7 + ctx.rnd() * 0.5;
      put(h, ctx, 'kh_skull', { lx: Math.cos(a) * r, lz: Math.sin(a) * r * 0.8 }, { scale: 1.1 + ctx.rnd() * 0.3, yaw: faceYaw(h, (ctx.rnd() - 0.5) * 1.6), dy: i === 0 ? 1.0 : 0.55 - r * 0.25, tint: BONE, layer: 'hero', castShadow: false, r: 0.5, h: 0.9 });
    }
    scatterAround(h, ctx, ['kh_bone_A', 'kh_bone_C'], 4, 2.2, [1.2, 1.5], BONE);
  },
  weapon_rack: (h, ctx) => {
    put(h, ctx, 'proc:rack', { lx: 0, lz: 0 }, { r: 0.6, h: 2.4 });
    put(h, ctx, 'kd_sword_shield', { lx: 1.3, lz: -0.5 }, { scale: 1.3, dy: 2.9, r: 0.3, h: 1.4 });
    put(h, ctx, 'kd_barrel_small', { lx: -2.0, lz: 0.4 }, { scale: 1.3, tint: [0.92, 0.9, 0.86], r: 0.6, h: 1.3 });
  },
  war_table: (h, ctx) => {
    put(h, ctx, 'kd_table_long_tablecloth_decorated_A', { lx: 0, lz: 0 }, { scale: 1.2, yaw: faceYaw(h, Math.PI / 2), r: 1.3, h: 2.2 });
    for (const [lx, lz] of [
      [-1.6, 0.9],
      [1.6, 0.9],
      [-1.6, -0.9],
    ] as const)
      put(h, ctx, 'kd_stool', { lx, lz }, { scale: 1.35, yaw: ctx.rnd() * 6.28, layer: 'detail', castShadow: false, r: 0.4, h: 0.6 });
  },
  egg_clutch: (h, ctx) => {
    put(h, ctx, 'proc:straw', { lx: 0, lz: 0 }, { scale: 1.9, layer: 'hero', castShadow: false, r: 1.4, h: 0.3 });
    const n = 6 + Math.floor(ctx.rnd() * 3);
    for (let i = 0; i < n; i++) {
      const a = ctx.rnd() * Math.PI * 2;
      const r = ctx.rnd() * 0.9;
      put(h, ctx, 'proc:egg', { lx: Math.cos(a) * r, lz: Math.sin(a) * r * 0.8 }, { scale: 0.9 + ctx.rnd() * 0.5, yaw: ctx.rnd() * 6.28, tilt: (ctx.rnd() - 0.5) * 0.5, dy: 0.12, castShadow: false, r: 0.4, h: 0.8 });
    }
    put(h, ctx, 'proc:eggshell', { lx: 1.3, lz: 0.6 }, { scale: 1.1, yaw: ctx.rnd() * 6.28, layer: 'detail', castShadow: false, r: 0.4, h: 0.3 });
    put(h, ctx, 'proc:brazier', { lx: -1.7, lz: -0.3 }, { scale: 0.75, r: 0.7, h: 1.5 });
    ctx.out.flames.push({ pos: at(h, { lx: -1.7, lz: -0.3 }), y: ctx.surface(at(h, { lx: -1.7, lz: -0.3 })) + 1.3, w: 0.8, h: 1.3, cluster: h.cluster });
  },
  coiled_serpent: (h, ctx) => {
    put(h, ctx, 'proc:coil', { lx: 0, lz: -0.4 }, { r: 1.6, h: 6.5 });
  },
  poison_pool: (h, ctx) => {
    // Serpent-head spout on the wall behind the basin, lilies and bottles by the rim.
    const pool = ctx.arch.pools.find((p) => Math.hypot(p.center.x - h.pos.x, p.center.y - h.pos.y) < 0.01);
    const waterY = pool?.waterY ?? h.y - 0.3;
    put(h, ctx, 'proc:spout', { lx: 0, lz: -(h.spec.half[1] + 0.25) }, { y: h.y + 1.1, r: 0.5, h: 1, castShadow: false });
    for (let i = 0; i < 2; i++) put(h, ctx, 'kn_lily_large', { lx: (ctx.rnd() - 0.5) * 4, lz: (ctx.rnd() - 0.5) * 1.6 }, { y: waterY + 0.01, scale: 3.5 + ctx.rnd(), yaw: ctx.rnd() * 6.28, layer: 'detail', castShadow: false, tint: [0.7, 0.85, 0.62], r: 0.3, h: 0.1 });
  },
  idol: (h, ctx) => {
    put(h, ctx, 'proc:idol', { lx: 0, lz: 0 }, { tint: WHITE, r: 1.6, h: 4.4 });
    put(h, ctx, 'kd_coin_stack_medium', { lx: 1.2, lz: 1.2 }, { scale: 1.4, layer: 'detail', castShadow: false, r: 0.5, h: 0.6 });
    put(h, ctx, 'kd_coin_stack_small', { lx: -1.1, lz: 1.3 }, { scale: 1.4, layer: 'detail', castShadow: false, r: 0.5, h: 0.5 });
  },
  brazier: (h, ctx) => {
    put(h, ctx, 'proc:brazier', { lx: 0, lz: 0 }, { r: 0.9, h: 2 });
  },
  gold_pile: (h, ctx) => {
    put(h, ctx, 'kd_chest_gold', { lx: 0, lz: -0.3 }, { scale: 1.35, r: 0.9, h: 1.6, tint: GOLD });
    put(h, ctx, 'kd_coin_stack_large', { lx: 1.3, lz: 0.3 }, { scale: 1.4, r: 0.9, h: 1.4, yaw: 0.6 });
    put(h, ctx, 'kd_coin_stack_medium', { lx: -1.3, lz: 0.4 }, { scale: 1.4, r: 0.6, h: 0.9, yaw: 2.1 });
    put(h, ctx, 'kd_coin_stack_small', { lx: 0.4, lz: 0.9 }, { scale: 1.4, r: 0.6, h: 0.6, yaw: 4, layer: 'detail', castShadow: false });
  },
  war_totem: (h, ctx) => {
    put(h, ctx, 'kh_post_skull', { lx: 0, lz: 0 }, { scale: 1.25, r: 0.7, h: 4.1 });
    put(h, ctx, 'kh_skull', { lx: 0.5, lz: 0.6 }, { scale: 1.1, tint: BONE, layer: 'detail', castShadow: false, r: 0.4, h: 0.9 });
    put(h, ctx, 'kd_candle_melted', { lx: -0.5, lz: 0.6 }, { scale: 1.4, layer: 'detail', castShadow: false, r: 0.2, h: 0.9 });
  },
  drum: (h, ctx) => {
    put(h, ctx, 'proc:drum', { lx: 0, lz: 0 }, { r: 1.1, h: 1.4 });
  },
  altar_dais: (h, ctx) => {
    put(h, ctx, 'proc:dais', { lx: 0, lz: 0 }, { r: 1.7, h: 2.2 });
    for (const lx of [-2.6, 2.6]) put(h, ctx, 'kh_shrine_candles', { lx, lz: 0.9 }, { scale: 1.2, dy: 0.5, r: 0.6, h: 1.8 });
    put(h, ctx, 'kh_skull_candle', { lx: 0.9, lz: 0.2 }, { scale: 1.1, dy: 1.5, layer: 'detail', castShadow: false, r: 0.4, h: 1.2 });
  },
  fang_jaw: (h, ctx) => {
    put(h, ctx, 'proc:fang_jaw', { lx: 0, lz: 0 }, { r: 0.8, h: 5.5 });
  },
  // +dungeon2: monastery / mine wing heroes ------------------------------------------------
  serpent_fountain: (h, ctx) => {
    put(h, ctx, 'proc:fountain', { lx: 0, lz: 0 }, { y: h.y, r: 3.5, h: 5.2 });
  },
  bookcase: (h, ctx) => {
    put(h, ctx, 'proc:bookcase', { lx: 0, lz: -0.15 }, { r: 0.5, h: 3.9 });
    if (ctx.rnd() < 0.6) put(h, ctx, 'kd_candle_triple', { lx: 1.9, lz: 0.2 }, { scale: 1.4, layer: 'detail', castShadow: false, r: 0.2, h: 0.9 });
    if (ctx.rnd() < 0.5) put(h, ctx, 'kd_stool', { lx: -1.9, lz: 0.4 }, { scale: 1.3, yaw: ctx.rnd() * 6.28, layer: 'detail', castShadow: false, r: 0.4, h: 0.6 });
  },
  lectern: (h, ctx) => {
    put(h, ctx, 'proc:lectern', { lx: 0, lz: 0 }, { r: 0.6, h: 1.8 });
    put(h, ctx, 'kd_candle_lit', { lx: 0.9, lz: -0.4 }, { scale: 1.5, layer: 'detail', castShadow: false, r: 0.2, h: 1 });
    ctx.out.flames.push({ pos: at(h, { lx: 0.9, lz: -0.4 }), y: ctx.surface(at(h, { lx: 0.9, lz: -0.4 })) + 1.45, w: 0.35, h: 0.6, cluster: h.cluster });
  },
  mine_cart: (h, ctx) => {
    // Rails run along the wall (local x), a lantern on a post beside them.
    put(h, ctx, 'proc:mine_cart', { lx: 0, lz: 0 }, { yaw: faceYaw(h, Math.PI / 2), r: 1, h: 2 });
    put(h, ctx, 'kh_lantern_standing', { lx: 3.1, lz: 0.2 }, { scale: 1.5, r: 0.4, h: 1.4 });
    ctx.out.flames.push({ pos: at(h, { lx: 3.1, lz: 0.2 }), y: ctx.surface(at(h, { lx: 3.1, lz: 0.2 })) + 0.9, w: 0.4, h: 0.6, cluster: h.cluster });
    ctx.out.glows.push({ pos: at(h, { lx: 3.1, lz: 1.6 }), y: h.y, r: 4.2, temp: 'warm', strength: 0.6, cluster: h.cluster });
  },
  ore_pile: (h, ctx) => {
    put(h, ctx, 'proc:ore_pile', { lx: 0, lz: 0 }, { r: 1, h: 1 });
    put(h, ctx, 'kd_box_small_decorated', { lx: 2.0, lz: -0.2 }, { scale: 1.2, yaw: faceYaw(h, 0.4), r: 0.7, h: 1.4 });
  },
  sarcophagus: (h, ctx) => {
    put(h, ctx, 'proc:sarcophagus', { lx: 0, lz: 0 }, { yaw: faceYaw(h, Math.PI / 2), r: 0.9, h: 1.5 });
    for (const lx of [-2.3, 2.3]) put(h, ctx, 'kd_candle_melted', { lx, lz: 0.3 }, { scale: 1.5, layer: 'detail', castShadow: false, r: 0.2, h: 0.9 });
  },
  arch: (h, ctx) => archPosts(h, ctx, false),
  arch_gold: (h, ctx) => archPosts(h, ctx, true),
};

/** Serpent columns either side of a corridor mouth (outside the strip) with a vine fringe. */
function archPosts(h: PlacedHint, ctx: HeroContext, gold: boolean): void {
  const c = ctx.arch.corridors.find((x) => x.id === h.owner);
  const half = (c?.axis.width ?? 10) / 2 + 0.85;
  const scale = h.scale;
  for (const sgn of [-1, 1]) {
    const p = at({ ...h, scale: 1 }, { lx: half * sgn, lz: 0 });
    const y = ctx.surface(p);
    ctx.place({ asset: gold ? 'proc:column_gold' : 'proc:column_serpent', pos: p, y, yaw: faceYaw(h), scale, tint: WHITE, layer: 'hero', r: 0.8, h: 4.6, castShadow: true });
    for (let i = 0; i < 3; i++) {
      const sc = 3.2 + ctx.rnd() * 1.2;
      const q = at({ ...h, scale: 1 }, { lx: half * sgn + (ctx.rnd() - 0.5) * 0.8, lz: (ctx.rnd() - 0.5) * 0.8 });
      ctx.place({ asset: 'kn_hanging_moss', pos: q, y: y + 4.6 * scale - 1.6, yaw: ctx.rnd() * 6.28, scale: sc, stretch: [0.75, 1.4, 1], tint: [0.38, 0.46, 0.32], layer: 'detail', r: 0.2, h: 1.6, castShadow: false });
    }
  }
}
